import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router';
import { useParentPathwayId, useParentCaseId, useParentNodeId, usePathwayName } from '@/stores/pathwayStore';
import { addCustomModel } from '@/api/registry';
import { buildInternalsModulePayload } from '@/components/nodes/internalsModulePayload';
import { registryActions } from '@/stores/registryStore';
import { parseErrorMessage } from '@/utils/errorUtils';
import { toast } from '@/utils/toast';
import SaveInternalsModuleModal from '@/components/nodes/SaveInternalsModuleModal';
import FloppyDiskIcon from '@/assets/floppy-disk.svg';

const SaveInternalsButton = () => {
  const { projectId } = useParams();
  const parentPathwayId = useParentPathwayId();
  const parentCaseId = useParentCaseId();
  const parentNodeId = useParentNodeId();
  const pathwayName = usePathwayName();

  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  if (!parentPathwayId || !parentCaseId || !parentNodeId) {
    return null;
  }

  const handleSave = async ({ label, permissions, description }) => {
    setIsSaving(true);
    setErrors([]);
    try {
      await addCustomModel(
        buildInternalsModulePayload({ label, permissions, description, projectId, caseId: parentCaseId, nodeId: parentNodeId }),
      );
      await registryActions.refresh();
      setShowModal(false);
      toast.success(`Saved "${label}". You can now add it from the module list.`);
    } catch (error) {
      setErrors(parseErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setErrors([]);
          setShowModal(true);
        }}
        title="Save these internals as a reusable module"
        className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 hover:border-gray-400 text-gray-700 font-medium text-sm px-3 py-1.5 rounded-md shadow-sm"
      >
        <FloppyDiskIcon className="w-4 h-4 shrink-0" />
        Save as New Module
      </button>
      {showModal && createPortal(
        <SaveInternalsModuleModal
          show
          onCancel={() => setShowModal(false)}
          onSave={handleSave}
          isLoading={isSaving}
          errorMessages={errors}
          defaultLabel={pathwayName}
        />,
        document.body,
      )}
    </>
  );
};

export default SaveInternalsButton;
